const FAST_WINDOW_MS = 60_000;
const ACTIVE_WINDOW_MS = 300_000;
const FAST_INTERVAL_MS = 5_000;
const SLOW_INTERVAL_MS = 30_000;
const MAX_BACKOFF_MS = 60_000;

/**
 * Coordinate report refreshes without overlapping requests or polling hidden/offline tabs.
 * @param {Object} options
 * @param {(reportId: string, signal: AbortSignal) => Promise<Object>} options.fetchReport
 * @param {(report: Object) => void} options.onReport
 * @param {(error: unknown) => void} [options.onError]
 * @param {() => number} [options.now]
 * @param {(callback: () => void, delay: number) => unknown} [options.setTimeoutFn]
 * @param {(timer: unknown) => void} [options.clearTimeoutFn]
 * @param {Document} [options.documentRef]
 * @param {Window} [options.windowRef]
 * @param {Navigator} [options.navigatorRef]
 * @param {() => AbortController} [options.createAbortController]
 */
export function createAdaptiveReportPoller({
  fetchReport,
  onReport,
  onError = () => {},
  now = () => Date.now(),
  setTimeoutFn = globalThis.setTimeout.bind(globalThis),
  clearTimeoutFn = globalThis.clearTimeout.bind(globalThis),
  documentRef = globalThis.document,
  windowRef = globalThis,
  navigatorRef = globalThis.navigator,
  createAbortController = () => new AbortController(),
} = {}) {
  if (typeof fetchReport !== "function") throw new TypeError("fetchReport is required");
  if (typeof onReport !== "function") throw new TypeError("onReport is required");

  let reportId = null;
  let startedAt = null;
  let timer = null;
  let inFlight = null;
  let errorCount = 0;
  let destroyed = false;

  const isAvailable = () => documentRef?.hidden !== true && navigatorRef?.onLine !== false;
  const clearTimer = () => {
    if (timer !== null) {
      clearTimeoutFn(timer);
      timer = null;
    }
  };
  const cancelRequest = () => {
    if (inFlight) inFlight.controller.abort();
    inFlight = null;
  };
  const baseDelay = () => {
    if (startedAt === null) return null;
    const elapsed = Math.max(0, now() - startedAt);
    if (elapsed < FAST_WINDOW_MS) return FAST_INTERVAL_MS;
    if (elapsed < ACTIVE_WINDOW_MS) return SLOW_INTERVAL_MS;
    return null;
  };
  const nextDelay = () => {
    const delay = baseDelay();
    if (delay === null) return null;
    const backoff = errorCount === 0 ? 0 : Math.min(MAX_BACKOFF_MS, 1_000 * 2 ** (errorCount - 1));
    return Math.max(delay, backoff);
  };
  const schedule = () => {
    clearTimer();
    if (destroyed || reportId === null || !isAvailable()) return;
    const delay = nextDelay();
    if (delay === null) return;
    timer = setTimeoutFn(() => {
      timer = null;
      void runRefresh();
    }, delay);
  };
  async function runRefresh() {
    if (destroyed || reportId === null || !isAvailable() || inFlight) return;
    const activeId = reportId;
    const controller = createAbortController();
    const request = { controller, reportId: activeId };
    inFlight = request;
    try {
      const report = await fetchReport(activeId, controller.signal);
      if (inFlight === request && reportId === activeId && !destroyed) {
        errorCount = 0;
        await onReport(report);
      }
    } catch (error) {
      if (
        !controller.signal.aborted && inFlight === request && reportId === activeId && !destroyed
      ) {
        errorCount += 1;
        onError(error);
      }
    } finally {
      if (inFlight === request) {
        inFlight = null;
        schedule();
      }
    }
  }
  const requestRefresh = () => {
    if (destroyed || reportId === null || !isAvailable()) return;
    clearTimer();
    void runRefresh();
  };
  const onVisibilityChange = () => {
    if (documentRef?.hidden !== true) requestRefresh();
    else clearTimer();
  };
  const onFocus = () => requestRefresh();
  const onOnline = () => requestRefresh();
  const onOffline = () => clearTimer();

  const addListeners = () => {
    documentRef?.addEventListener?.("visibilitychange", onVisibilityChange);
    windowRef?.addEventListener?.("focus", onFocus);
    windowRef?.addEventListener?.("online", onOnline);
    windowRef?.addEventListener?.("offline", onOffline);
  };
  const removeListeners = () => {
    documentRef?.removeEventListener?.("visibilitychange", onVisibilityChange);
    windowRef?.removeEventListener?.("focus", onFocus);
    windowRef?.removeEventListener?.("online", onOnline);
    windowRef?.removeEventListener?.("offline", onOffline);
  };
  addListeners();

  return {
    start(nextReportId, { immediate = false } = {}) {
      if (destroyed) return;
      clearTimer();
      cancelRequest();
      reportId = nextReportId;
      startedAt = now();
      errorCount = 0;
      if (immediate) requestRefresh();
      else schedule();
    },
    stop() {
      clearTimer();
      cancelRequest();
      reportId = null;
      startedAt = null;
      errorCount = 0;
    },
    refresh() {
      requestRefresh();
    },
    markMutation({ refresh = true } = {}) {
      if (reportId === null) return;
      startedAt = now();
      errorCount = 0;
      if (refresh) requestRefresh();
      else schedule();
    },
    destroy() {
      if (destroyed) return;
      destroyed = true;
      clearTimer();
      cancelRequest();
      removeListeners();
      reportId = null;
      startedAt = null;
    },
    getState() {
      return {
        reportId,
        startedAt,
        nextDelay: nextDelay(),
        timerActive: timer !== null,
        inFlight: inFlight !== null,
      };
    },
  };
}

export const reportPollerTimings = Object.freeze({
  FAST_WINDOW_MS,
  ACTIVE_WINDOW_MS,
  FAST_INTERVAL_MS,
  SLOW_INTERVAL_MS,
});
